import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Check, Loader2, Crown, ArrowRight, RefreshCw } from 'lucide-react';

export default function SubscriptionSuccessPage() {
  const { user, updateUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking | activated | pending | error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!user) return;
    checkActivation();
  }, [user]);

  const checkActivation = async () => {
    try {
      const res = await api.get('/subscription/check-activation');
      if (res.data.activated) {
        setStatus('activated');
        updateUser({
          subscription_status: res.data.subscription_status,
          subscription_plan: res.data.subscription_plan,
          subscription_end_date: res.data.subscription_end_date,
        });
      } else if (attempts < 10) {
        setStatus('pending');
        setAttempts(prev => prev + 1);
        setTimeout(checkActivation, 3000);
      } else {
        setStatus('pending');
      }
    } catch {
      setStatus('error');
    }
  };

  const retryCheck = () => {
    setStatus('checking');
    setAttempts(0);
    checkActivation();
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 size={48} className="mx-auto text-amber-500 animate-spin mb-6" />
            <h1 className="font-serif text-2xl text-obsidian mb-3" data-testid="success-checking-title">
              Confirming your payment...
            </h1>
            <p className="text-muted-foreground text-sm font-light">
              Please wait while we activate your subscription. This usually takes a few seconds.
            </p>
          </>
        )}

        {status === 'activated' && (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center mb-6">
              <Check size={40} className="text-emerald-600" />
            </div>
            <h1 className="font-serif text-2xl md:text-3xl text-obsidian mb-3" data-testid="success-activated-title">
              Subscription activated!
            </h1>
            <p className="text-muted-foreground text-sm font-light mb-2">
              Your <span className="font-medium text-obsidian capitalize">{user?.subscription_plan}</span> plan is now active.
            </p>
            {user?.subscription_end_date && (
              <p className="text-xs text-muted-foreground mb-8">
                Renews on {new Date(user.subscription_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/creator-dashboard">
                <Button
                  data-testid="success-dashboard-btn"
                  className="rounded-full h-12 px-8 bg-obsidian hover:bg-amber-600 transition-colors font-medium text-sm"
                >
                  <Crown size={16} className="mr-2" />
                  Go to Dashboard
                </Button>
              </Link>
              <Link to="/feed">
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-8 border-border/60 hover:border-amber-400 text-sm"
                >
                  Start Creating
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center mb-6">
              <Crown size={36} className="text-amber-600" />
            </div>
            <h1 className="font-serif text-2xl text-obsidian mb-3" data-testid="success-pending-title">
              Payment received!
            </h1>
            <p className="text-muted-foreground text-sm font-light mb-6">
              Your payment was successful. Your subscription is being activated — this may take up to a minute.
            </p>
            <Button
              onClick={retryCheck}
              variant="outline"
              className="rounded-full h-10 px-6 text-sm border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="retry-check-btn"
            >
              <RefreshCw size={14} className="mr-2" />
              Check Again
            </Button>
            <p className="text-xs text-muted-foreground mt-4">
              If your subscription isn't activated within a few minutes, please contact support.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="font-serif text-2xl text-obsidian mb-3">Something went wrong</h1>
            <p className="text-muted-foreground text-sm mb-6">
              We couldn't verify your subscription. Please try again or contact support.
            </p>
            <Button onClick={retryCheck} className="rounded-full h-10 px-6 text-sm bg-obsidian hover:bg-amber-600">
              <RefreshCw size={14} className="mr-2" /> Retry
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
